/**
 * ManagementAddOnCard
 *
 * Compact add-on card for management surfaces. Like `ManagementTierCard`,
 * replaces two near-identical bespoke Card JSX blocks (Group's read-only
 * overview + Solo's click-to-toggle view) with one shared primitive.
 *
 * Two interaction modes — picked by whether `onToggle` is provided:
 *   - Read-only (Group): no checkbox, no click; status chip shows seat
 *     usage ("3 of 12"); optional subtotal line for the aggregate cost.
 *   - Selectable (Solo): checkbox in the header, whole card is clickable.
 *     `isIncluded` locks the checkbox + status when the user's selected
 *     tier already grants the add-on.
 *
 * Marketing-style `AddOnCard` continues to serve checkout / browse flows.
 */

import React from 'react';
import { Box, Card, CardContent, Checkbox, Chip, Typography } from '@mui/material';

import { useDisplayBundle } from '../bundleDisplay';
import type { BillingInterval, BundleWithStatus } from '../types';
import { formatPrice, getMonthlyAmountCents } from '../utils';

export interface ManagementAddOnCardProps {
  bundle: BundleWithStatus;
  billingInterval: BillingInterval;
  /**
   * Right-side status chip in the header. Group uses "3 of 12" / "None";
   * Solo uses "Included" / "Active" / "Available".
   */
  statusChip?: { label: string; color?: 'primary' | 'default' };
  /**
   * Drives the card's emphasis (2px solid primary + paper vs 1px solid +
   * grey). Active = "this add-on is in use" (Group) or "this add-on is
   * selected" (Solo).
   */
  isActive?: boolean;
  /**
   * Solo: covered by the selected tier's grants — checkbox forced on,
   * locked, price replaced with "Included in tier". Ignored when `onToggle`
   * is not provided.
   */
  isIncluded?: boolean;
  /**
   * When provided, renders a checkbox in the header and makes the whole
   * card clickable. Solo uses this; Group omits it (read-only).
   */
  onToggle?: (bundleId: string) => void;
  /**
   * Subtotal line shown below the price when set + > 0. Group uses this
   * to surface the per-add-on aggregate ("Subtotal: $XX/mo").
   */
  subtotalCents?: number;
  /**
   * Suffix appended after the unit price. Defaults to "/mo".
   * Group uses "/member/mo" since add-ons are per-seat on the team plan.
   */
  priceSuffix?: string;
}

export const ManagementAddOnCard: React.FC<ManagementAddOnCardProps> = ({
  bundle: rawBundle,
  billingInterval,
  statusChip,
  isActive = false,
  isIncluded = false,
  onToggle,
  subtotalCents,
  priceSuffix = '/mo',
}) => {
  const bundle = useDisplayBundle(rawBundle) as BundleWithStatus;
  const priceCents = getMonthlyAmountCents(bundle, billingInterval === 'year');
  const selectable = !!onToggle;
  const checkboxChecked = isActive || isIncluded;
  const checkboxDisabled = isIncluded;

  const handleClick = () => {
    if (isIncluded || !onToggle) return;
    onToggle(bundle.bundle_id);
  };

  return (
    <Card
      data-testid={`addon-card-${bundle.bundle_id}`}
      onClick={selectable ? handleClick : undefined}
      role={selectable && !isIncluded ? 'button' : undefined}
      tabIndex={selectable && !isIncluded ? 0 : undefined}
      onKeyDown={
        selectable
          ? e => {
              if (isIncluded) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
              }
            }
          : undefined
      }
      sx={theme => ({
        height: '100%',
        // Matches ManagementTierCard — 8px radius for the softer "card-y"
        // look in dense management grids.
        borderRadius: 2,
        border: isActive ? '2px solid' : '1px solid',
        borderColor: isActive ? 'primary.main' : 'divider',
        backgroundColor: isActive ? 'background.paper' : 'grey.50',
        cursor: selectable && !isIncluded ? 'pointer' : 'default',
        transition: theme.transitions.create(['border-color', 'background-color']),
        '&:hover':
          selectable && !isIncluded ? { borderColor: 'primary.light' } : undefined,
      })}
    >
      <CardContent sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 0.5,
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {selectable && (
              <Checkbox
                size="small"
                checked={checkboxChecked}
                disabled={checkboxDisabled}
                onChange={() => onToggle?.(bundle.bundle_id)}
                onClick={e => e.stopPropagation()}
                sx={{ p: 0 }}
              />
            )}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {bundle.name}
            </Typography>
          </Box>
          {statusChip && (
            <Chip
              label={statusChip.label}
              size="small"
              color={statusChip.color ?? 'default'}
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 22, flexShrink: 0 }}
            />
          )}
        </Box>
        {bundle.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            {bundle.description}
          </Typography>
        )}
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: isIncluded ? 'success.main' : 'primary.main' }}
        >
          {isIncluded
            ? 'Included in tier'
            : (
              <>
                {formatPrice(priceCents, bundle.currency)}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: 'text.secondary' }}
                >
                  {' '}
                  {priceSuffix}
                </Typography>
              </>
            )}
        </Typography>
        {subtotalCents !== undefined && subtotalCents > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5 }}
          >
            Subtotal: {formatPrice(subtotalCents, bundle.currency)}/mo
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
